import { Fragment, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { UpstackDataGrid } from '../UpstackDataGrid/UpstackDataGrid';
import { Adjustment } from '../../../store/Adjustment';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../store/GeneralStore';
import { DetailViewCard } from '../CardWithTitle';
import { DefaultAdjustmentGridColumnDefs } from '../../Pages/Adjustment/AdjustmentGridColumnDefs';

export interface AdjustmentMiniTableParams {
  id: string;
  fetchFunction: (
    filters?: ApiAdvancedFilter,
    forceRefresh?: boolean
  ) => Promise<PaginatedResponse<Adjustment> | undefined | void>;
  dataSubject: () => BehaviorSubject<PaginatedResponse<Adjustment>>;
  loadingSubject: () => BehaviorSubject<boolean>;
}

export const AdjustmentMiniTable = ({ id, fetchFunction, dataSubject, loadingSubject }: AdjustmentMiniTableParams) => {
  const [adjustments, setAdjustments] = useState<PaginatedResponse<Adjustment>>({} as PaginatedResponse<Adjustment>);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(true);

  const fetchAdjustments = async (filters?: ApiAdvancedFilter, loading?: boolean, forceRefresh?: boolean) => {
    fetchFunction(
      {
        page: 1,
        pageSize: 50,
        ...filters,
        filters: [
          ...(filters?.filters || []),
          {
            field: 'assetUpstackId',
            operator: 'equals',
            value: id
          }
        ]
      },
      forceRefresh
    );
  };

  useEffect(() => {
    const dataSub = dataSubject().subscribe((r) => {
      if (!tableLoading) setAdjustments(r);
      setLoading(false);
    });

    const loadingSub = loadingSubject().subscribe((loading) => setTableLoading(loading));

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, [tableLoading]);

  useEffect(() => {
    if (id) fetchAdjustments(undefined, false, true);
  }, [id]);

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    height: 'inherit'
  };

  const filteredCols = DefaultAdjustmentGridColumnDefs().filter((col) => col.field !== 'assetNumber');
  const filterKeys = filteredCols.filter((c) => c.filterable).map((c) => c.field);

  return (
    <DetailViewCard
      enableCollapse
      key="adjustment-table-card"
      title="Adjustments"
      dataTestId="adjustment-view-card"
      className="mt-4">
      <Fragment>
        {loading && <ComponentSpinner label="Loading Adjustment Data" />}
        {!loading && (
          <UpstackDataGrid
            rows={adjustments.data || []}
            sxStyles={sxStyle}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 50 } },
              sorting: { sortModel: [{ field: 'runDate', sort: 'desc' }] },
              pinnedColumns: { left: ['details', 'runDate'] }
            }}
            filterKeys={filterKeys}
            currentPage={adjustments.page}
            rowCount={adjustments.totalCount || 0}
            columns={filteredCols}
            dataCy="adjustment-mini-data-grid-pagination"
            fetchFunction={fetchAdjustments}
            title="adjustment"
            showSearch={false}
            page="adjustmentGrid_MiniTable"
            apiFilter
            rowPrimaryKey="commissionAdjustmentId"
            gridSettings={{ muiConfig: {}, disabledFields: [] }}
            pageSizeOptions={[50]}
            showToolbar={false}
            withoutSave
            localLoading={tableLoading}
            pagination={adjustments.totalPages > 1}
            advancedFiltering
          />
        )}
      </Fragment>
    </DetailViewCard>
  );
};
